/* import __COLOCATED_TEMPLATE__ from './phone-call-alert.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type TwilioService from 'embercom/services/twilio-service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import type PhoneNumber from 'embercom/models/inbox/phone-number';
import { tracked } from '@glimmer/tracking';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type Session from 'embercom/services/session';
import type Calling from 'embercom/models/settings/calling';
import { get, post } from 'embercom/lib/ajax';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';

// Max time to wait for the decline cache write before rejecting the Twilio call.
// Measured POST latency is ~129ms; 150ms stays under the 200ms perception threshold.
const DECLINE_CACHE_WRITE_TIMEOUT_MS = 150;

interface Args {}

interface Signature {
  Args: Args;
}

export default class PhoneCallAlertComponent extends Component<Signature> {
  @service declare twilioService: TwilioService;
  @service declare intercomCallService: IntercomCallService;
  @service declare intl: IntlService;
  @service declare session: Session;
  @tracked selectedPhoneNumber: PhoneNumber | undefined;
  @tracked escalationSummary: string | null = null;
  @tracked countdownSeconds = 10;
  @tracked callSettings: Calling | null = null;
  @tracked isAcceptingCall = false;

  private autoAcceptTimer: number | null = null;
  private countdownTimer: number | null = null;
  private autoAnswerWorker: Worker | null = null;
  private modalRenderedAt: number = Date.now();
  private modalDiagnosticSent = false;
  private diagnosticConversationId: number | undefined;
  private diagnosticCallType: string | undefined;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.modalRenderedAt = Date.now();
    this.diagnosticConversationId =
      this.twilioService.conversation?.id ?? this.twilioService.incomingConversationId;
    this.diagnosticCallType = this.twilioService.incomingCallType;
    this.fetchEscalationSummary();
    this.loadCallSettings();
    this.resolveCallbackDefault();
    if (this.twilioService.autoAnswerEnabled) {
      this.startAutoAnswerTimerWithFallback();
    }
  }

  willDestroy() {
    super.willDestroy();
    this.terminateAutoAnswerWorker();
    this.clearAutoAcceptTimer();
    this.clearCountdownTimer();
    this.sendModalRenderedDiagnostic('cancelled');
  }

  private startAutoAcceptTimer() {
    this.clearAutoAcceptTimer();
    this.autoAcceptTimer = window.setTimeout(() => {
      this.acceptCall();
    }, 10000); // 10 seconds
  }

  private startCountdownTimer() {
    this.clearCountdownTimer();
    this.countdownSeconds = 10;

    this.countdownTimer = window.setInterval(() => {
      this.countdownSeconds--;
      if (this.countdownSeconds <= 0) {
        this.clearCountdownTimer();
      }
    }, 1000); // Update every second
  }

  private clearAutoAcceptTimer() {
    if (this.autoAcceptTimer) {
      clearTimeout(this.autoAcceptTimer);
      this.autoAcceptTimer = null;
    }
  }

  private clearCountdownTimer() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private get isWebWorkerAutoAnswerEnabled(): boolean {
    return this.session.workspace?.isFeatureEnabled('phone-auto-answer-web-worker') ?? false;
  }

  private startAutoAnswerWorker() {
    this.terminateAutoAnswerWorker();
    this.countdownSeconds = 10;

    let workerUrl = assetUrl('/assets/workers/auto-answer-timer.js');
    this.autoAnswerWorker = new Worker(workerUrl);

    this.autoAnswerWorker.onmessage = (event: MessageEvent) => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      let data = event.data;
      if (data.type === 'tick') {
        this.countdownSeconds = data.remaining;
      } else if (data.type === 'complete') {
        this.countdownSeconds = 0;
        this.acceptCall();
        this.terminateAutoAnswerWorker();
      }
    };

    this.autoAnswerWorker.onerror = () => {
      // Fallback: accept call immediately on worker failure rather than
      // silently failing to auto-answer (which would cause teammate_missed)
      this.acceptCall();
      this.terminateAutoAnswerWorker();
    };

    this.autoAnswerWorker.postMessage({ type: 'start', countdownSeconds: 10 });
  }

  private startAutoAnswerTimerWithFallback() {
    if (this.isWebWorkerAutoAnswerEnabled) {
      try {
        this.startAutoAnswerWorker();
      } catch {
        this.startAutoAcceptTimer();
        this.startCountdownTimer();
      }
    } else {
      this.startAutoAcceptTimer();
      this.startCountdownTimer();
    }
  }

  private terminateAutoAnswerWorker() {
    if (this.autoAnswerWorker) {
      this.autoAnswerWorker.postMessage({ type: 'cancel' });
      this.autoAnswerWorker.terminate();
      this.autoAnswerWorker = null;
    }
  }

  get participants() {
    return this.twilioService.participants;
  }

  get userSummary() {
    return this.twilioService.userSummary;
  }

  get incomingPhoneAlertMessage() {
    if (this.twilioService.isIncomingCallConference) {
      if (this.twilioService.participants.length < 2) {
        return this.intl.t(
          'calling.incoming-phone-call-modal.conference-call-unknown-participants',
        );
      }

      let participantList = this.participants
        .slice(0, -1)
        .map((participant) => participant.displayLabel)
        .join(', ');
      let lastParticipant = this.participants.lastObject?.displayLabel;

      return this.intl.t('calling.incoming-phone-call-modal.conference-call-known-participants', {
        participantList,
        lastParticipant,
      });
    }

    if (!this.userSummary) {
      // Fast modal: userSummary may not be available yet if callerPhone was not
      // sent by the backend. Show a generic message rather than throwing.
      return this.intl.t('calling.incoming-phone-call-modal.unknown-number', {
        number: this.twilioService.callerPhone || '',
      });
    } else if (this.isCallerIdBlocked(this.userSummary.phone) === true) {
      return this.intl.t('calling.incoming-phone-call-modal.blocked-number');
    } else if (!this.userSummary.name) {
      if (this.twilioService.isIncomingCallback) {
        return this.intl.t('calling.incoming-phone-call-modal.callback-unknown-number', {
          number: this.userSummary.phone,
        });
      }
      if (
        this.twilioService.isIncomingCallTransfer ||
        this.twilioService.isIncomingCallWarmTransfer
      ) {
        return this.intl.t('calling.incoming-phone-call-modal.transfer-unknown-number', {
          number: this.userSummary.phone,
        });
      }
      return this.intl.t('calling.incoming-phone-call-modal.unknown-number', {
        number: this.userSummary.phone,
      });
    }
    if (
      this.twilioService.isIncomingCallTransfer ||
      this.twilioService.isIncomingCallWarmTransfer
    ) {
      return this.intl.t('calling.incoming-phone-call-modal.transfer-known-number', {
        name: this.userSummary.name,
        number: this.userSummary.phone,
      });
    }

    if (this.twilioService.isIncomingCallback) {
      return this.intl.t('calling.incoming-phone-call-modal.callback-known-number', {
        name: this.userSummary.name,
        number: this.userSummary.phone,
      });
    }
    return this.intl.t('calling.incoming-phone-call-modal.known-number', {
      name: this.userSummary.name,
      number: this.userSummary.phone,
    });
  }

  get currentPhoneNumberTitle() {
    return this.intl.t('calling.modal.current-phone-number', {
      number: this.twilioService.workspacePhoneNumber,
      country: this.twilioService.calledNumberCountryCode,
    });
  }

  get isForceJoinCall() {
    return this.twilioService.autoAnswerEnabled || (this.callSettings?.autoAnswerEnabled ?? false);
  }

  get autoJoinCallWarning() {
    if (!this.isForceJoinCall) {
      return null;
    }

    return this.intl.t('calling.incoming-phone-call-modal.warning-on-force-join-call', {
      seconds: this.countdownSeconds,
    });
  }

  isCallerIdBlocked(phone: string | undefined) {
    return phone !== undefined && phone === '+266696687';
  }

  @action
  async acceptCall() {
    this.isAcceptingCall = true;
    this.sendModalRenderedDiagnostic('accepted');
    // Capture conversationId BEFORE calling acceptCall() because it resets conversation state
    let conversationId =
      this.twilioService.conversation?.id ?? this.twilioService.incomingConversationId;

    await this.twilioService.acceptCall();

    post(`/ember/phone_call/record_accept_inbound_phone_call`, {
      app_id: this.session.workspace.id,
      admin_id: this.session.teammate.id,
      conversation_id: conversationId,
    });
  }

  @action
  async onNumberSelectionChange(option: PhoneNumber) {
    this.selectedPhoneNumber = option;
  }

  @action
  async rejectCall() {
    this.sendModalRenderedDiagnostic('rejected');
    // Capture conversationId BEFORE calling rejectCall() because it resets conversation state
    let conversationId =
      this.twilioService.conversation?.id ?? this.twilioService.incomingConversationId;

    if (this.session.workspace?.isFeatureEnabled('phone-explicit-decline-signal')) {
      let timeout = new Promise((resolve) => setTimeout(resolve, DECLINE_CACHE_WRITE_TIMEOUT_MS));
      await Promise.race([
        post(`/ember/phone_call/record_decline_inbound_phone_call`, {
          app_id: this.session.workspace.id,
          admin_id: this.session.teammate.id,
          conversation_id: conversationId,
        }).catch(() => {
          /* Swallow errors — fallback to generic decline message is acceptable */
        }),
        timeout,
      ]);
    }

    await this.twilioService.rejectCall();

    if (!this.session.workspace?.isFeatureEnabled('phone-explicit-decline-signal')) {
      post(`/ember/phone_call/record_decline_inbound_phone_call`, {
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
        conversation_id: conversationId,
      });
    }
  }

  @action
  navigateToConversation() {
    this.twilioService.navigateToConversation();
  }

  @action
  ignoreCallback() {
    this.sendModalRenderedDiagnostic('ignored');
    this.twilioService.ignoreCallback();
  }

  @action
  async acceptCallback() {
    this.sendModalRenderedDiagnostic('accepted');
    this.twilioService.acceptCallback(undefined, this.selectedPhoneNumber?.phoneNumber);
  }

  private async sendModalRenderedDiagnostic(dismissAction: string) {
    if (this.modalDiagnosticSent) {
      return;
    }
    if (!this.session.workspace?.isFeatureEnabled('phone-incoming-call-modal-diagnostics')) {
      return;
    }
    this.modalDiagnosticSent = true;
    try {
      await post('/ember/twilio_device_presence_diagnostics', {
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
        event_type: 'call_modal_rendered',
        conversation_id: this.diagnosticConversationId,
        call_type: this.diagnosticCallType,
        tab_hidden: document.hidden,
        dismiss_action: dismissAction,
        modal_display_duration_ms: Date.now() - this.modalRenderedAt,
      });
    } catch (e: unknown) {
      // Silently fail — diagnostics should not impact main functionality
    }
  }

  async loadCallSettings() {
    try {
      this.callSettings = await this.intercomCallService.getCallSettings();

      if (this.isForceJoinCall && !this.autoAcceptTimer && !this.autoAnswerWorker) {
        this.startAutoAnswerTimerWithFallback();
      }
    } catch (error) {
      console.error('Failed to load call settings:', error);
      // Fallback to false if settings can't be loaded
      this.callSettings = null;
    }
  }

  private async resolveCallbackDefault() {
    if (!this.twilioService.isIncomingCallback) {
      return;
    }
    let conversationId =
      this.twilioService.conversation?.id ?? this.twilioService.incomingConversationId;
    if (!conversationId) {
      return;
    }

    try {
      let data = await get('/ember/phone_call/callback', {
        app_id: this.session.workspace.id,
        conversation_id: conversationId,
      });
      let resolved = data.resolved_outbound_number || data.call?.workspace_phone_number;
      if (resolved && !this.selectedPhoneNumber) {
        let match = this.intercomCallService.workspacePhoneNumbers?.findBy('phoneNumber', resolved);
        if (match) {
          this.selectedPhoneNumber = match;
        }
      }
    } catch {
      // Keep firstObject default on failure
    }
  }

  @action
  async fetchEscalationSummary() {
    let conversationId =
      this.twilioService.conversation?.id ?? this.twilioService.incomingConversationId;
    if (!conversationId) {
      return;
    }
    this.escalationSummary = await this.intercomCallService.fetchEscalationSummary(conversationId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::PhoneCallAlert': typeof PhoneCallAlertComponent;
    'inbox2/phone-call-alert': typeof PhoneCallAlertComponent;
  }
}
